import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, ClipboardList, Users, FileText, Gauge, CalendarCheck2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const usd = (n) => `US$${Math.round(n).toLocaleString()}`;
const relColor = (v) => (v >= 85 ? "#059669" : v >= 75 ? "#0B2B5C" : v >= 60 ? "#D97706" : "#DC2626");
const th = "px-4 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground font-semibold";

const Section = ({ title, sub, children, testid }) => (
  <section className="mb-10" data-testid={testid}>
    <h3 className="text-base font-semibold tracking-tight mb-1">{title}</h3>
    {sub && <p className="text-xs text-muted-foreground mb-4">{sub}</p>}
    {children}
  </section>
);

const Tbl = ({ head, rows, testid }) => (
  <div className="border border-border overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm min-w-[560px]">
      <thead><tr className="bg-slate-50 text-left">{head.map((h, i) => <th key={i} className={th}>{h}</th>)}</tr></thead>
      <tbody>{rows}</tbody>
    </table>
  </div>
);

export const BusinessReview = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [gran, setGran] = useState("monthly");

  useEffect(() => { api.get("/supply-chain/business-review").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  if (err) return <div className="border border-border bg-white p-10 text-center text-sm text-muted-foreground">Unable to load the business review.</div>;
  if (!data) return <div className="border border-border bg-white p-16 text-center font-mono-plex text-xs tracking-widest uppercase text-muted-foreground animate-pulse">Loading business review…</div>;

  const g = data.granularities?.[gran];
  if (!g) return null;
  const kpi = [
    { l: "Containers", v: g.summary.containers },
    { l: "Total Spend", v: usd(g.summary.spend_usd) },
    { l: "Avg Utilisation", v: `${g.summary.avg_utilisation}%` },
    { l: "On-Time", v: `${g.summary.on_time_pct}%` },
  ];

  return (
    <div data-testid="business-review">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="text-sm text-muted-foreground max-w-2xl">{data.meta.subtitle}</p>
        <Select value={gran} onValueChange={setGran}>
          <SelectTrigger data-testid="business-review-granularity" className="w-[160px] bg-white border border-border rounded-md font-mono-plex text-xs tracking-widest uppercase"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly" data-testid="br-monthly">Monthly</SelectItem>
            <SelectItem value="quarterly" data-testid="br-quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Section title={`${gran === "monthly" ? "Monthly" : "Quarterly"} review agenda`} sub="Standing agenda for the review meeting" testid="br-agenda">
        <div className="border border-border bg-white p-6">
          <ul className="space-y-3">
            {g.agenda.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm" data-testid={`br-agenda-${i}`}>
                <CalendarCheck2 className="h-4 w-4 mt-0.5 text-[#0B2B5C] shrink-0" strokeWidth={1.5} /><span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <section className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-border mb-10">
        {kpi.map((k, i) => (
          <div key={i} className="border-r border-b border-border bg-white p-5" data-testid={`br-kpi-${i}`}>
            <div className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground mb-2">{k.l}</div>
            <div className="font-display text-2xl xl:text-3xl tracking-tight">{k.v}</div>
          </div>
        ))}
      </section>

      <Section title="Container volume & cost trend" sub={`TEU and freight cost by ${gran === "monthly" ? "month" : "quarter"}`}>
        <div className="border border-border bg-white p-6">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={g.trend} margin={{ top: 5, right: 10, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="period" stroke="#64748B" fontSize={11} tickLine={false} axisLine={{ stroke: "#CBD5E1" }} />
              <YAxis yAxisId="l" stroke="#64748B" fontSize={11} axisLine={false} />
              <YAxis yAxisId="r" orientation="right" stroke="#64748B" fontSize={11} axisLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, n) => (n === "Cost" ? usd(v) : v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="l" name="TEU" dataKey="teu" fill="#0B2B5C" radius={[3, 3, 0, 0]} barSize={38} />
              <Line yAxisId="r" name="Cost" type="monotone" dataKey="cost_usd" stroke="#0072B8" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section title="Container volume & cost by destination">
        <Tbl head={["Destination", "TEU", "Containers", "Cost"]} rows={g.destinations.map((d, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`br-dest-${i}`}>
            <td className="px-4 py-2.5 font-medium">{d.destination}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{d.teu}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{d.containers}</td>
            <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{usd(d.cost_usd)}</td>
          </tr>
        ))} />
      </Section>

      <Section title="Container utilisation by M³ & weight × customer">
        <Tbl head={["Customer", "Avg M³", "Avg Weight (t)", "Utilisation"]} rows={g.utilisation.map((u, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`br-util-${i}`}>
            <td className="px-4 py-2.5 font-medium">{u.customer}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{u.avg_m3}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{u.avg_weight_t}</td>
            <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: relColor(u.utilisation_pct) }}>{u.utilisation_pct}%</td>
          </tr>
        ))} />
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        <Section title="Total LCL cost & volume × country">
          <Tbl head={["Country", "Volume (M³)", "Cost"]} rows={g.lcl.map((l, i) => (
            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`br-lcl-${i}`}>
              <td className="px-4 py-2.5 font-medium">{l.country}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{l.volume_m3.toLocaleString()}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{usd(l.cost_usd)}</td>
            </tr>
          ))} />
        </Section>
        <Section title="Transit-time performance × destination port">
          <Tbl head={["Port", "Planned", "Actual", "On-Time"]} rows={g.transit.map((t, i) => (
            <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`br-transit-${i}`}>
              <td className="px-4 py-2.5 font-medium">{t.port}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{t.planned_days}d</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{t.actual_days}d</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: relColor(t.on_time_pct) }}>{t.on_time_pct}%</td>
            </tr>
          ))} />
        </Section>
      </div>

      <Section title="Rate schedule & ETA changes" sub="Main trade lanes — for invoice vetting by PACT Group, including ETA movements">
        <Tbl head={["Lane", "Service", "Prev ETA", "New ETA", "Δ Days", "Note"]} rows={g.eta_changes.map((e, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`br-eta-${i}`}>
            <td className="px-4 py-2.5 font-medium text-left">{e.lane}</td>
            <td className="px-4 py-2.5 text-right text-muted-foreground">{e.service}</td>
            <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{e.prev_eta}</td>
            <td className="px-4 py-2.5 text-right tabular-nums">{e.new_eta}</td>
            <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: e.delta_days > 0 ? "#DC2626" : e.delta_days < 0 ? "#059669" : "#64748B" }}>{e.delta_days > 0 ? `+${e.delta_days}` : e.delta_days}</td>
            <td className="px-4 py-2.5 text-left text-muted-foreground">{e.note}</td>
          </tr>
        ))} />
      </Section>

      <p className="text-[11px] text-muted-foreground/60 font-mono-plex tracking-widest uppercase">{data.meta.note}</p>
    </div>
  );
};

export const AccountStructure = () => {
  const escalation = [
    { level: "Level 1", contact: "Import Specialist", when: "Bookings, documentation & operational exceptions · < 1 business day" },
    { level: "Level 2", contact: "Operations & Customs — Drazen Basar / David Cooke", when: "Operational escalations & customs matters · < 2 business days" },
    { level: "Level 3", contact: "Raghav Kapoor / Paul Dimitriadis / Mat Vermuelen", when: "Service, commercial or strategic matters" },
  ];
  return (
    <div data-testid="account-structure">
      <p className="text-sm text-muted-foreground max-w-2xl mb-6">The dedicated SGL account team supporting PACT Group's export program, and the agreed escalation path.</p>
      <Section title="Your group of skilled specialists" testid="account-team">
        <figure className="border border-border bg-white overflow-hidden" data-testid="account-team-slide">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_10bbf65d-5234-494d-80af-d82aa44de1fd/artifacts/o3c1f2ae_image.webp"
            alt="SGL account management structure — your group of skilled specialists for PACT Group"
            loading="lazy"
            className="w-full h-auto block"
          />
        </figure>
      </Section>
      <Section title="Escalation matrix" testid="account-escalation">
        <Tbl head={["Level", "Contact", "When / SLA"]} rows={escalation.map((e, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`account-esc-${i}`}>
            <td className="px-4 py-3 font-mono-plex text-xs">{e.level}</td>
            <td className="px-4 py-3 text-right font-medium">{e.contact}</td>
            <td className="px-4 py-3 text-left text-muted-foreground">{e.when}</td>
          </tr>
        ))} />
      </Section>
    </div>
  );
};

const KPI_LIST = [
  { label: "Container booking accuracy & timeliness", target: "100% accuracy · 95% within 24–48 hrs" },
  { label: "Container Pick Up & Drop Off accuracy", target: "98%" },
  { label: "Transit Time adherence to plan", target: "75%" },
  { label: "Invoice accuracy to Quoted rates", target: "95%" },
  { label: "Response time to operational issues / questions", target: "Within 24 hrs" },
];

const CLIENT_KPI_LIST = [
  { label: "Order Management — Dispatch vs Required Departure", target: "≥ 95%" },
  { label: "Port-to-Port Transit Time as per Agreement", target: "≥ 95%" },
  { label: "Customs Clearance prior to Arrival", target: "100%" },
  { label: "Delivery vs Actual Arrival", target: "≥ 95%" },
  { label: "Door-to-Door Transit", target: "≥ 90%" },
  { label: "Billing Accuracy", target: "≥ 98%" },
  { label: "Billing Timeliness", target: "≥ 95%" },
  { label: "Delivered in Full and On Time (DIFOT)", target: "≥ 95%" },
];

const SLIDE_BASE = "https://customer-assets-0z36b82j.emergentagent.net/job_3746c8ad-73a3-4cbb-87f3-7b3af248f392/artifacts";

const BOOKING_SLIDES = [
  { id: "bkp4ozo2", caption: "The three-stage export operating model" },
  { id: "wge4gzzj", caption: "End-to-end process — from client instruction to shipment closure" },
  { id: "2j93zngd", caption: "SGL offering — 12 coordinated workstreams" },
  { id: "qwilcwgl", caption: "Document control matrix — every document, every shipment" },
  { id: "em5yyohz", caption: "Service levels & KPIs — measurable performance on every shipment", src: "https://static.prod-images.emergentagent.com/jobs/3746c8ad-73a3-4cbb-87f3-7b3af248f392/images/a71e534eff7414d50850a377a40eede6c5bf42c8d587f693f7689e51de6c542e.jpeg" },
];

export const OperationalRelated = () => {
  const [view, setView] = useState("kpis");
  const [kpiTab, setKpiTab] = useState("sgl");
  return (
    <div data-testid="operational-related">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <p className="text-sm text-muted-foreground max-w-2xl">Service KPIs, the booking process and invoicing parameters agreed between SGL and PACT Group.</p>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger data-testid="operational-view" className="w-[210px] bg-white border border-border rounded-md font-mono-plex text-xs tracking-widest uppercase"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="kpis" data-testid="op-kpis">KPIs</SelectItem>
            <SelectItem value="booking" data-testid="op-booking">Booking Process</SelectItem>
            <SelectItem value="invoice" data-testid="op-invoice">Invoice Parameters</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {view === "kpis" && (
        <div data-testid="op-kpis-panel">
          <div className="flex flex-wrap items-center gap-1 border-b border-border mb-6">
            {[{ k: "sgl", l: "SGL KPIs" }, { k: "client", l: "Client KPI" }].map((t) => (
              <button key={t.k} onClick={() => setKpiTab(t.k)} data-testid={`op-kpi-tab-${t.k}`}
                className={`relative px-4 py-2.5 text-xs font-mono-plex tracking-widest uppercase transition-colors ${kpiTab === t.k ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {t.l}
                {kpiTab === t.k && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-[#0072B8]" />}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mb-6">{kpiTab === "sgl" ? "SGL service KPIs — measured and reviewed at the monthly business review." : "Client KPIs agreed with PACT Group — measured and reviewed at the monthly business review."}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(kpiTab === "sgl" ? KPI_LIST : CLIENT_KPI_LIST).map((k, i) => (
              <div key={i} className="border border-border bg-white p-5" data-testid={`op-kpi-${i}`}>
                <Gauge className="h-5 w-5 text-[#0B2B5C] mb-3" strokeWidth={1.5} />
                <div className="text-sm font-medium leading-snug">{k.label}</div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground">Target</span>
                  <span className="font-mono-plex text-[11px] tracking-wide uppercase text-[#0072B8] font-semibold text-right">{k.target}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === "booking" && (
        <Section title="Booking Process" sub="SGL's export booking & documentation SOP for PACT Group" testid="op-booking-panel">
          <div className="space-y-6">
            {BOOKING_SLIDES.map((s, i) => (
              <figure key={s.id} className="border border-border bg-white overflow-hidden" data-testid={`op-booking-slide-${i}`}>
                <img
                  src={s.src || `${SLIDE_BASE}/${s.id}_image.webp`}
                  alt={s.caption}
                  loading="lazy"
                  className="w-full h-auto block"
                />
                <figcaption className="border-t border-border px-5 py-3 font-mono-plex text-[10px] tracking-widest uppercase text-muted-foreground">
                  {String(i + 1).padStart(2, "0")} · {s.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>
      )}

      {view === "invoice" && (
        <Section title="Invoice Parameters" testid="op-invoice-panel">
          <div className="border border-border bg-white p-6">
            <div className="flex items-center gap-2 mb-3"><FileText className="h-4 w-4 text-[#0B2B5C]" strokeWidth={1.5} /><h4 className="text-sm font-semibold">1. Method of Charging and Invoicing</h4></div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">The Service Provider shall invoice for the following services on a <span className="text-foreground font-medium">weekly basis within 24 hours of container loading at the PACT Group site</span>.</p>
            <ul className="space-y-2">
              {["Destination costs", "Ocean Freight and THC", "Agency Fees"].map((x, i) => (
                <li key={i} className="flex items-center gap-3 text-sm" data-testid={`op-invoice-item-${i}`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" strokeWidth={1.5} />{x}
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}
    </div>
  );
};
